import { Link } from 'react-router-dom';
import { useTranslation } from '../../../i18n/useTranslation';
import { routes } from '../../../Utils/routes';
import { LegalPage, type LegalSection } from '../LegalPage/LegalPage';

/**
 * Terms of Service. The text is the agreement itself, carried over word for word
 * from the page it replaces: a rewrite during a rebuild would change what
 * people already agreed to.
 *
 * English only, deliberately. This is the version that binds, and translating
 * it would create a second one that might not say the same thing.
 */
const SECTIONS: LegalSection[] = [
  {
    heading: '1. Acceptance',
    blocks: [
      { kind: 'p', text: 'By accessing or using Magbit, you agree to these Terms of Service. If you do not agree, do not use the service.' }
    ]
  },
  {
    heading: '2. The service',
    blocks: [
      { kind: 'p', text: 'Magbit provides tools to help private tutors organize students, lessons, payments, and optional Google Calendar synchronization. Magbit is not an accounting, payment-processing, legal, or tax service.' }
    ]
  },
  {
    heading: '3. Subscriptions and billing',
    blocks: [
      { kind: 'p', text: (
        <>
          Some features (“Magbit Premium”) require a paid subscription, billed monthly or yearly as described on our{' '}
          <Link to={routes.pricing}>pricing page</Link>. Payments are processed by Paddle, our merchant of record, which
          handles checkout, billing, taxes, and payment data; your purchase is also subject to{' '}
          <a href="https://www.paddle.com/legal/checkout-buyer-terms" rel="noopener">Paddle's buyer terms</a>. New
          subscriptions may include a free trial; unless cancelled before the trial ends, the subscription starts and
          renews automatically until cancelled. You can cancel at any time from the subscription page in the app; access
          continues until the end of the period already paid for. Refunds are handled according to our{' '}
          <Link to={routes.refunds}>refund policy</Link>.
        </>
      ) }
    ]
  },
  {
    heading: '4. Your responsibilities',
    blocks: [
      { kind: 'p', text: 'You are responsible for the information you enter, for obtaining any permissions required to store information about your students, and for keeping your account credentials secure. You must not use Magbit for unlawful activity or attempt to interfere with the service.' }
    ]
  },
  {
    heading: '5. Google services',
    blocks: [
      { kind: 'p', text: 'If you connect Google Calendar, you authorize Magbit to use the permissions you approve solely to provide calendar synchronization. Your use of Google services is also subject to Google’s applicable terms and policies. You may revoke Magbit’s Google access at any time through your Google Account permissions.' }
    ]
  },
  {
    heading: '6. Availability and changes',
    blocks: [
      { kind: 'p', text: 'We may modify, suspend, or discontinue parts of Magbit as the service evolves. We will make reasonable efforts to maintain availability, but uninterrupted or error-free operation is not guaranteed.' }
    ]
  },
  {
    heading: '7. Disclaimer',
    blocks: [
      { kind: 'p', text: 'Magbit is provided on an “as is” and “as available” basis. To the extent permitted by law, we disclaim warranties of merchantability, fitness for a particular purpose, and non-infringement.' }
    ]
  },
  {
    heading: '8. Limitation of liability',
    blocks: [
      { kind: 'p', text: 'To the extent permitted by law, Magbit will not be liable for indirect, incidental, special, consequential, or punitive damages, or for loss of data, profits, or business opportunity arising from use of the service.' }
    ]
  },
  {
    heading: '9. Termination',
    blocks: [
      { kind: 'p', text: 'You may stop using Magbit at any time. We may suspend or terminate access if these terms are violated or if necessary to protect the service, users, or applicable law.' }
    ]
  },
  {
    heading: '10. Changes to these terms',
    blocks: [
      { kind: 'p', text: 'We may update these terms from time to time. Continued use after an update takes effect means you accept the updated terms.' }
    ]
  },
  {
    heading: '11. Contact',
    blocks: [
      { kind: 'p', text: (
        <>For questions about these terms, contact <a href="mailto:rakloze@gmail.com">rakloze@gmail.com</a>.</>
      ) }
    ]
  }
];

const UPDATED = '23 July 2026';

export function Terms() {
  const { tr } = useTranslation();
  return (
    <LegalPage
      title="Terms of Service"
      updated={UPDATED}
      sections={SECTIONS}
      back={<Link className="ghost-button" to={routes.home}>{tr('back')}</Link>}
    />
  );
}
